import { useMemo } from "react";
import type { DashboardPayload } from "../types";

function DashboardSidebar({
  dashboard,
  sourceCount,
  latestPeriod,
  buildFingerprint,
  isOpen,
  onClose,
  onCreditOpen,
}: {
  dashboard: DashboardPayload;
  sourceCount: number;
  latestPeriod: string;
  buildFingerprint: number;
  isOpen: boolean;
  onClose: () => void;
  onCreditOpen: () => void;
}) {
  const tabIndex = isOpen ? undefined : -1;
  const barcodeCode = useMemo(() => {
    const compactPeriod = latestPeriod.replace("-", "");
    const compactSchema = dashboard.schemaVersion.replace(/\./g, "");
    return `45 ${compactPeriod} ${compactSchema} ${buildFingerprint % 10}`;
  }, [buildFingerprint, dashboard.schemaVersion, latestPeriod]);

  return (
    <aside id="dashboard-sidebar" className="dashboard-sidebar" aria-label="ダッシュボードサイドバー" aria-hidden={!isOpen}>
      <div className="sidebar-header">
        <a className="sidebar-brand" href="https://www.jma-jp.org/" target="_blank" rel="noreferrer" tabIndex={tabIndex}>
          <img className="brand-mark" src="/brand/jma-mark.svg" alt="" aria-hidden="true" />
          <span className="brand-copy">
            {/* 「マーケティング・コンパス」が中途半端な位置で折り返されないよう、
                「・」区切りで1行ずつ表示する(読み上げは元のタイトルのまま)。 */}
            <span className="brand-name" aria-label={dashboard.title}>
              {dashboard.title.split("・").map((part) => (
                <span key={part} className="brand-name__line" aria-hidden="true">
                  {part}
                </span>
              ))}
            </span>
            <span className="brand-subtitle">JAPAN MARKETING ASSOC.</span>
          </span>
        </a>
        <button className="sidebar-icon-button" type="button" aria-label="サイドバーを閉じる" onClick={onClose} tabIndex={tabIndex}>
          <span className="sidebar-icon sidebar-icon--close" aria-hidden="true" />
        </button>
      </div>

      <div className="sidebar-rule" aria-hidden="true" />

      <p className="sidebar-group-label">指標グループ / GROUPS</p>
      <nav className="sidebar-tabs" aria-label="ダッシュボードタブ">
        <a className="sidebar-tab is-active" href="#indicators" aria-current="page" tabIndex={tabIndex}>
          <span className="sidebar-tab__mark" aria-hidden="true" />
          <span className="sidebar-tab__label">基本指標</span>
          <span className="sidebar-tab__count">{String(dashboard.indicators.length).padStart(2, "0")}</span>
        </a>
      </nav>

      <div className="sidebar-fill" />

      <dl className="sidebar-stats" aria-label="ダッシュボード概要">
        <div>
          <dt>基準月</dt>
          <dd>{latestPeriod}</dd>
        </div>
        <div>
          <dt>指標</dt>
          <dd>{String(dashboard.indicators.length).padStart(2, "0")}</dd>
        </div>
        <div>
          <dt>出典</dt>
          <dd>{String(sourceCount).padStart(2, "0")}</dd>
        </div>
        <div>
          <dt>SCHEMA</dt>
          <dd>{dashboard.schemaVersion}</dd>
        </div>
      </dl>

      <div className="sidebar-barcode">
        <div className="sidebar-barcode__label">
          <span>DATA BUILD</span>
          <span>指紋</span>
        </div>
        <Barcode seed={buildFingerprint} />
        <div className="sidebar-barcode__code">{barcodeCode}</div>
      </div>

      <div className="sidebar-footer">
        <button className="sidebar-credit-button" type="button" onClick={onCreditOpen} tabIndex={tabIndex}>
          このサービスについて
        </button>
      </div>
    </aside>
  );
}

function Barcode({ seed }: { seed: number }) {
  const bars = useMemo(() => {
    let state = seed >>> 0;

    const next = () => {
      state = (state + 0x6d2b79f5) >>> 0;
      let t = state;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };

    const widths = [1, 1.5, 2, 2.5];
    const gaps = [1, 1.5, 2];
    const width = 200;
    const generated: Array<{ key: number; x: number; y: number; w: number; h: number }> = [];
    let x = 3;
    let index = 0;

    while (x < width - 3) {
      const w = widths[Math.floor(next() * widths.length)];
      const tall = index % 9 === 0;
      generated.push({ key: index, x, y: tall ? 0 : 2, w, h: tall ? 28 : 24 });
      x += w + gaps[Math.floor(next() * gaps.length)];
      index += 1;
    }

    return generated;
  }, [seed]);

  return (
    <svg viewBox="0 0 200 28" className="sidebar-barcode__svg" preserveAspectRatio="none" aria-hidden="true">
      {bars.map((bar) => (
        <rect key={bar.key} x={bar.x} y={bar.y} width={bar.w} height={bar.h} fill="#16181A" />
      ))}
    </svg>
  );
}

function SidebarToggleButton({ isOpen, onClick }: { isOpen: boolean; onClick: () => void }) {
  return (
    <button
      className="sidebar-toggle"
      type="button"
      aria-controls="dashboard-sidebar"
      aria-expanded={isOpen}
      aria-label={isOpen ? "サイドバーを閉じる" : "サイドバーを開く"}
      onClick={onClick}
    >
      <span className="sidebar-icon sidebar-icon--menu" aria-hidden="true" />
    </button>
  );
}

export { DashboardSidebar, Barcode, SidebarToggleButton };
